/**
 * Per-errorCode message builders for reservation finalization failures.
 *
 * Kept separate from useSubmitReservation so the hook stays focused on
 * the request/response plumbing, while string composition for specific
 * server error codes lives next to the resx keys it depends on.
 */

import type { MinimumViolation } from "../utils/productQuantities";

/** Returns the trimmed string when non-empty, otherwise null. */
function nonEmptyString(value: unknown): string | null {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Fills `{0}`/`{1}`/`{2}`/`{3}` tokens in a resx template. Shared by the
 * server-driven message (metadata from the finalize response) and the
 * client-side pre-check message (built from `getMinimumViolations`) so both
 * surfaces compose text the same way and cannot drift apart.
 */
function fillTemplate(template: string, tokens: Record<string, string>): string {
  return template.replace(/\{(\d+)\}/g, (match, i) =>
    i in tokens ? tokens[i] : match,
  );
}

/**
 * Copy for the per-ticket variant of the minimum-quantity message.
 *
 * Two templates rather than one: Greek cannot hedge the way "ticket(s)" does in
 * English, and "το/τα εισιτήριο/α" reads like a form letter. The count decides
 * which noun form is used, so it has to reach the template choice.
 */
export interface PerTicketMinimumCopy {
  /** Names exactly one ticket. */
  single: string;
  /** Names two or more. */
  plural: string;
  /** Conjunction before the last number: "and" / "και". */
  and: string;
}

/**
 * "2" / "2 and 3" / "2, 3 and 4" — the last pair joined by the conjunction, the
 * rest by commas. Matches how a person would read the list aloud in both
 * languages; neither puts a comma before the conjunction here.
 */
function formatTicketList(numbers: Array<number | string>, and: string): string {
  if (numbers.length <= 1) return numbers.join("");
  const head = numbers.slice(0, -1);
  const last = numbers[numbers.length - 1];
  return `${head.join(", ")} ${and} ${last}`;
}

/** The template whose noun form agrees with how many tickets are named. */
function pickTemplate(copy: PerTicketMinimumCopy, count: number): string {
  return count === 1 ? copy.single : copy.plural;
}

/**
 * Build the user-facing message for `ProductMinimumQuantityNotMet` (40007).
 *
 * The merchant can configure a custom error message per product in Teller.
 * When the BE supplies that custom text via `response.message`, we show it
 * verbatim. Otherwise we fall back to a localized template, filling
 * `{0}` / `{1}` / `{2}` / `{3}` from the metadata XElement the BE attaches:
 *
 *   <metadata>
 *     <product id name required inCart perTicket="1" tickets="2,3"/>
 *   </metadata>
 *
 * which the JSON-via-XML serializer exposes as
 *   { product: { "@id": ..., "@name": ..., "@required": ..., "@inCart": ...,
 *                "@perTicket": ..., "@tickets": ... } }
 *
 * `perTicket`/`tickets` are only present for per-ticket products (see
 * `PLAN-per-ticket-minimums.md` D4); when `@perTicket === "1"` the per-ticket
 * template is used instead of the order-wide one, filling `{3}` with the
 * 1-based ticket numbers the BE already comma-joined.
 */
export function formatProductMinimumQuantityMessage(
  customMessage: unknown,
  template: string,
  perTicketCopy: PerTicketMinimumCopy,
  metadata: Record<string, any> | undefined,
): string {
  const custom = nonEmptyString(customMessage);
  if (custom) return custom;

  const p = metadata?.product ?? {};
  const isPerTicket = p["@perTicket"] === "1";

  // The BE comma-joins the ticket numbers; split them back out so the count can
  // pick the noun form and the conjunction can go in the right place.
  const ticketNumbers = String(p["@tickets"] ?? "")
    .split(",")
    .map((n) => n.trim())
    .filter((n) => n.length > 0);

  const tokens: Record<string, string> = {
    "0": String(p["@name"] ?? ""),
    "1": String(p["@required"] ?? ""),
    "2": String(p["@inCart"] ?? "0"),
    "3": formatTicketList(ticketNumbers, perTicketCopy.and),
  };
  return fillTemplate(
    isPerTicket ? pickTemplate(perTicketCopy, ticketNumbers.length) : template,
    tokens,
  );
}

/**
 * Client-side pre-check mirror of `formatProductMinimumQuantityMessage`,
 * built from `getMinimumViolations` (per-ticket products only) instead of
 * server metadata. Used by the pay-handler pre-check in reservation-page.tsx
 * so a submit that would fail with 40007 is caught before the round-trip —
 * the server stays authoritative and still runs the same check.
 *
 * Reports the first offending product only (D4 does not ask for a list of
 * products); a merchant's `minimumQuantityErrorMessage` for that product
 * still wins verbatim, exactly like `response.message` does server-side.
 */
export function formatClientMinimumQuantityMessage(
  violations: MinimumViolation[],
  ticketNumberMap: Map<number, number>,
  perTicketCopy: PerTicketMinimumCopy,
): string {
  if (violations.length === 0) return "";

  const product = violations[0].product;
  const custom = nonEmptyString(product.minimumQuantityErrorMessage);
  if (custom) return custom;

  const productViolations = violations.filter((v) => v.product === product);
  const ticketNumbers = Array.from(
    new Set(productViolations.map((v) => v.ticketId)),
  )
    .map((ticketId) => ticketNumberMap.get(ticketId) ?? ticketId)
    .sort((a, b) => a - b);

  const tokens: Record<string, string> = {
    "0": product.name,
    "1": String(productViolations[0].required),
    // {2} is what the offending ticket holds, matching the server's inCart. The
    // shipped copy does not use it, but a merchant template or a future
    // translation might, and an unfilled token would render as literal "{2}".
    "2": String(productViolations[0].held),
    "3": formatTicketList(ticketNumbers, perTicketCopy.and),
  };
  return fillTemplate(pickTemplate(perTicketCopy, ticketNumbers.length), tokens);
}
